import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useEnvironments } from 'services/useEnvironments';
import { useSetNavbarVisibility } from 'services/useNavbarVisibility';
import { useSelectedEnvironment } from 'services/useSelectedEnvironment';
import { useSelectedProject } from 'services/useSelectedProject';
import { Project } from 'core/models/project';
import { Environment } from 'core/models/environment';
import { Vehicle } from 'core/models/vehicle';
import { RTCPageTemplate } from 'components/views/organisms/RTCPageTemplate';
import { useAutowareVizOverMQTT } from 'services/viz/useAutowareVizOverMQTT';
import { ignore } from '@tier4/autowareviz-core';
import { useVehicleRTCRequest } from 'services/useVehicleRTCRequest';
import { MovableFrame } from 'components/views/atoms/MovableFrame';
import { RTCControlPanel } from '../../../views/molecules/RTCControlPanel';
import { useRTCAccept } from '../../../../services/useRTCAccept';
import { FileDropArea } from './FileDropArea';

type RTCPageImplProps = {
    projectId: Project.Id;
    environmentId: Environment.Id;
    vehicleId: Vehicle.Id;
};

function RTCPageImpl(props: RTCPageImplProps) {
    const { projectId, environmentId, vehicleId } = props;

    const showNavbar = useSetNavbarVisibility();
    useEffect(() => {
        showNavbar(false);
        return () => showNavbar(true);
    }, [showNavbar]);

    const engine = useAutowareVizOverMQTT(vehicleId, projectId);

    const tasks = useVehicleRTCRequest(vehicleId, projectId);
    const accept = useRTCAccept(vehicleId, projectId, environmentId);

    const [files, setFiles] = useState<File[]>();

    if (!projectId || !environmentId) {
        return <p>Please select project and environment</p>;
    }

    return (
        <FileDropArea onFiles={setFiles}>
            <>
                {
                    engine
                        ? <RTCPageTemplate engine={engine} tasks={tasks} files={files} />
                        : <>Loading</>
                }
                <MovableFrame
                    title='RTC Controller'
                    resizable={false}
                    left={10}
                    top={10}
                >
                    <RTCControlPanel
                        task={tasks[0]}
                        onAccept={task => accept(task, !task.activated)}
                    />
                </MovableFrame>
            </>
        </FileDropArea>
    );
}

export type RTCPageProps = {
    //
}

export function RTCPage(props: RTCPageProps) {
    ignore(props);

    const { vehicleId } = useParams<{ vehicleId: string }>();
    if (vehicleId === undefined) throw Error('missing vehicle ID in URL');

    const projectId = useSelectedProject()?.id;
    const environments = useEnvironments(projectId);
    const environmentId = useSelectedEnvironment(environments)?.id;

    if (!projectId || !environmentId) {
        return <p>Please select project and environment</p>;
    }

    return (
        <RTCPageImpl projectId={projectId} environmentId={environmentId} vehicleId={vehicleId} />
    );
}
